/*
 * BioIndex Lifeform selector.
 * One visible field. For an unknown genus, choose a kingdom INSIDE the popup.
 * Select one or several Lifeforms; persistence stays species.forms[].label
 * as a string for compatibility with all existing observation records.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  LIFEFORM_CHILDREN_BY_PARENT,
  lifeformDetailsFor,
  LIFEFORM_KINGDOMS,
  lifeformDescriptionFor,
  lifeformOptionsFor,
  parseLifeformSelection,
  resolveLifeformKingdom,
  serializeLifeformSelection,
  toggleLifeformSelection,
} from "../utils/formOptions";

const CONTROL_CLASS =
  "flex h-[48px] w-full min-w-0 items-center justify-between gap-3 rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm text-[#374049] transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 disabled:cursor-not-allowed disabled:opacity-50";

export default function LifeformPicker({
  scientificName = "",
  value = "",
  onChange,
  selectedKingdom = "",
  onKingdomChange,
  disabled = false,
  className = "",
}) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("lifeform");
  const [pendingKingdom, setPendingKingdom] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [panelStyle, setPanelStyle] = useState({});

  const inferredKingdom = resolveLifeformKingdom(scientificName);
  const kingdom =
    inferredKingdom || selectedKingdom || (open ? pendingKingdom : "");
  const choices = lifeformOptionsFor(kingdom);
  const savedValue = String(value || "").trim();
  // Refined animal and plant labels have nested sections, not legacy rows.
  // Previously saved labels such as "Bark" or "Leaves" remain visible only
  // if they are already stored in the observation.
  const legacyOptions = selectedOptions.filter(
    (item) =>
      !choices.includes(item) &&
      !Object.values(LIFEFORM_CHILDREN_BY_PARENT).some((details) =>
        details.includes(item),
      ),
  );
  // Include saved legacy choices in alphabetical order with the usual options.
  const visibleChoices = [...new Set([...choices, ...legacyOptions])].sort(
    (a, b) => a.localeCompare(b, "en", { sensitivity: "base" }),
  );

  const positionPanel = useCallback(() => {
    const element = triggerRef.current;
    if (!element || typeof window === "undefined") return;
    const bounds = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(
      Math.max(bounds.width, 225),
      Math.max(225, viewportWidth - 24),
    );
    const left = Math.max(
      12,
      Math.min(bounds.left, viewportWidth - width - 12),
    );
    const below = viewportHeight - bounds.bottom - 12;
    const above = bounds.top - 12;
    const showAbove = below < 230 && above > below;
    const maxHeight = Math.max(
      100,
      Math.min(330, (showAbove ? above : below) - 8),
    );

    setPanelStyle({
      position: "fixed",
      left,
      width,
      maxHeight,
      ...(showAbove
        ? { bottom: viewportHeight - bounds.top + 6 }
        : { top: bounds.bottom + 6 }),
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    positionPanel();

    function dismissWhenOutside(event) {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    function dismissOnEscape(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    function refreshPosition() {
      positionPanel();
    }

    document.addEventListener("pointerdown", dismissWhenOutside, true);
    document.addEventListener("keydown", dismissOnEscape, true);
    window.addEventListener("resize", refreshPosition);
    window.addEventListener("scroll", refreshPosition, true);
    return () => {
      document.removeEventListener("pointerdown", dismissWhenOutside, true);
      document.removeEventListener("keydown", dismissOnEscape, true);
      window.removeEventListener("resize", refreshPosition);
      window.removeEventListener("scroll", refreshPosition, true);
    };
  }, [open, positionPanel]);

  // A different scientific name may imply a different kingdom. Do not show
  // the previous organism's Lifeforms after the name changes.
  useEffect(() => {
    setOpen(false);
    setPendingKingdom("");
  }, [scientificName]);

  function toggleMenu() {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    const existing = parseLifeformSelection(savedValue);
    setSelectedOptions(existing);
    setPendingKingdom("");
    setStep(inferredKingdom || selectedKingdom ? "lifeform" : "kingdom");
    setOpen(true);
  }

  function chooseKingdom(nextKingdom) {
    setPendingKingdom(nextKingdom);
    setSelectedOptions([]);
    onKingdomChange?.(nextKingdom);
    setStep("lifeform");
    // Do not close: show Lifeform options immediately in this same menu.
  }

  function toggleLifeform(item) {
    setSelectedOptions((previous) => toggleLifeformSelection(previous, item));
  }

  function chooseLifeform(nextValue) {
    onChange?.(nextValue);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function applySelection() {
    chooseLifeform(serializeLifeformSelection(selectedOptions));
  }

  const panel =
    open && !disabled && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              zIndex: 9999,
              visibility: panelStyle.left === undefined ? "hidden" : "visible",
              ...panelStyle,
            }}
            role="dialog"
            aria-label={
              step === "kingdom"
                ? "Choose kingdom for Lifeform"
                : "Choose Lifeform"
            }
            className="flex flex-col overflow-hidden rounded-2xl border border-[#93ca87]/50 bg-white shadow-[0_16px_45px_rgba(0,0,0,0.18)]"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
          >
            {step === "kingdom" ? (
              <>
                <div className="shrink-0 border-b border-[#93ca87]/30 bg-[#FCFFF6] px-4 py-3 text-xs font-semibold text-[#374049]">
                  Which kingdom does this organism belong to?
                </div>
                <div className="min-h-0 overflow-y-auto overscroll-contain py-1">
                  {LIFEFORM_KINGDOMS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className="block w-full px-4 py-2.5 text-left text-sm text-[#374049] hover:bg-[#24c3be]/10 focus:bg-[#24c3be]/10 focus:outline-none"
                      onClick={() => chooseKingdom(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#93ca87]/30 bg-[#FCFFF6] px-3 py-2.5">
                  <span className="text-xs font-semibold text-[#374049]">
                    {kingdom ? `${kingdom} Lifeforms` : "Select Lifeform"}
                  </span>
                  {!inferredKingdom && (
                    <button
                      type="button"
                      className="rounded-md px-2 py-1 text-xs font-medium text-[#01767A] hover:bg-[#24c3be]/10"
                      onClick={() => setStep("kingdom")}
                    >
                      ← Back
                    </button>
                  )}
                </div>
                <div className="min-h-0 overflow-y-auto overscroll-contain py-1">
                  {visibleChoices.map((item) => {
                    const checked = selectedOptions.includes(item);
                    const legacy = legacyOptions.includes(item);
                    const description = lifeformDescriptionFor(item);
                    const details =
                      kingdom === "Animalia" || kingdom === "Plantae"
                        ? lifeformDetailsFor(item)
                        : [];
                    const groupActive =
                      details.length > 0 &&
                      (checked ||
                        details.some((detail) =>
                          selectedOptions.includes(detail),
                        ));
                    return (
                      <React.Fragment key={item}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={checked}
                          title={`${item}: ${description}`}
                          className={`flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm hover:bg-[#24c3be]/10 focus:bg-[#24c3be]/10 focus:outline-none ${
                            checked
                              ? "font-semibold text-[#01767A]"
                              : "text-[#374049]"
                          }`}
                          onClick={() => toggleLifeform(item)}
                        >
                          <span
                            aria-hidden="true"
                            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[11px] ${
                              checked
                                ? "border-[#24c3be] bg-[#24c3be] text-white"
                                : "border-[#93ca87]/60 bg-white"
                            }`}
                          >
                            {checked ? "✓" : ""}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block">
                              {item}
                              {legacy && (
                                <span className="ml-1 text-[11px] font-normal text-slate-500">
                                  (previously saved)
                                </span>
                              )}
                              {details.length > 0 && (
                                <span className="ml-1 text-[11px] font-normal text-[#01767A]">
                                  {groupActive ? "▾ Details" : "▸ More options"}
                                </span>
                              )}
                            </span>
                          </span>
                        </button>
                        {groupActive && (
                          <div className="border-y border-[#93ca87]/20 bg-[#FCFFF6]/70 py-1 pl-5 pr-1">
                            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#01767A]">
                              {item} details (optional; replaces the broad
                              label)
                            </div>
                            {details.map((detail) => {
                              const detailChecked =
                                selectedOptions.includes(detail);
                              const detailDescription =
                                lifeformDescriptionFor(detail);
                              return (
                                <button
                                  key={detail}
                                  type="button"
                                  role="checkbox"
                                  aria-checked={detailChecked}
                                  title={`${detail}: ${detailDescription}`}
                                  className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-[#24c3be]/10 focus:bg-[#24c3be]/10 focus:outline-none ${
                                    detailChecked
                                      ? "font-semibold text-[#01767A]"
                                      : "text-[#374049]"
                                  }`}
                                  onClick={() => toggleLifeform(detail)}
                                >
                                  <span
                                    aria-hidden="true"
                                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[11px] ${
                                      detailChecked
                                        ? "border-[#24c3be] bg-[#24c3be] text-white"
                                        : "border-[#93ca87]/60 bg-white"
                                    }`}
                                  >
                                    {detailChecked ? "✓" : ""}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block">{detail}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[#93ca87]/30 bg-[#FCFFF6] px-3 py-2">
                  <button
                    type="button"
                    className="rounded-md px-2 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100"
                    onClick={() => chooseLifeform("")}
                  >
                    Clear
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">
                      {selectedOptions.length} selected
                    </span>
                    <button
                      type="button"
                      className="rounded-lg bg-[#374049] px-4 py-2 text-xs font-semibold text-white hover:bg-[#01767A]"
                      onClick={applySelection}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`relative min-w-0 ${className}`}>
      <label className="mb-1 block text-xs font-semibold text-slate-600">
        Lifeform
      </label>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Lifeform"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggleMenu}
        disabled={disabled}
        className={CONTROL_CLASS}
      >
        <span
          className={`min-w-0 truncate ${savedValue ? "text-[#374049]" : "text-slate-400"}`}
        >
          {savedValue || "Select lifeform"}
        </span>
        <span aria-hidden="true" className="shrink-0 text-[#01767A]">
          ⌄
        </span>
      </button>
      {panel}
    </div>
  );
}
